import { getFirstQuestionOfSlide, useFormFactory } from '@meilleurtaux/form-factory'
import {
  Slide,
  SliderContainer,
  useDeviceDetection,
  useTracking,
} from '@meilleurtaux/form-factory-ds'
import { Fragment, useEffect, useRef, useState } from 'react'
import QuestionManagerForm from './QuestionManagerForm'
import type { Slide as SlideType } from '@meilleurtaux/form-factory'
import type { slideStatus } from 'utils/slideAnimation'
import { getSlideAnimation, isVerticalAnimation } from 'utils/slideAnimation'
import { submitStepForm } from 'utils/stepForm'

interface SlidesProps {
  toggleNextIsLoading: () => void
}

type status = slideStatus

interface StatefulSlide {
  slide: SlideType
  status: status
}

function Slides({ toggleNextIsLoading }: Readonly<SlidesProps>) {
  const {
    currentSlide,
    stepper,
    appearance,
    direction,
    isSlideValid,
    needToAddHistory,
    goTo,
    removeNeedAddHistory,
  } = useFormFactory(import.meta.env.VITE_API_URL)
  const { trackSlideChanged } = useTracking()
  const device = useDeviceDetection()
  const isVertical = isVerticalAnimation(
    device,
    appearance?.stepperPosition,
    appearance?.animationDirection,
  )
  const isFirstRenderRef = useRef(true)
  const previousSlideIdRef = useRef<number | null>(null)

  // on stocke les steps à afficher, qui seront agrémentées d'un statut, afin de déterminer l'animation à appliquer
  const [slides, setSlides] = useState<StatefulSlide[]>([])

  // handlePopState lit ces refs plutôt que currentSlide/isSlideValid directement : l'effet qui l'attache
  // ne doit s'exécuter qu'au montage (cf. plus bas), sous peine de désabonner/réabonner le listener 'popstate'
  // à chaque render et de perdre l'évènement si le navigateur le déclenche entre un cleanup et le setup suivant
  const currentSlideRef = useRef(currentSlide)
  currentSlideRef.current = currentSlide
  const isSlideValidRef = useRef(isSlideValid)
  isSlideValidRef.current = isSlideValid

  // on écoute le changement de slide pour mettre à jour notre état local
  useEffect(() => {
    const handlePopState = (ev: PopStateEvent) => {
      ev.preventDefault()
      // Si un hash est présent dans l'url, on va sur la slide correspondante
      let slideId = 1
      if (globalThis.location.hash) {
        slideId = Number.parseInt(globalThis.location.hash.slice(1))
        if (Number.isNaN(slideId)) {
          slideId = 1
        }
      }

      const currentSlide = currentSlideRef.current
      const isSlideValid = isSlideValidRef.current

      // On regarde si on essaye d'aller sur une slide qui est après la slide actuelle et que la slide actuelle n'est pas valide
      // Si c'est le cas, on averti l'utilisateur qu'il ne peut pas revenir en arrière ou passer des étapes
      if (slideId > (currentSlide?.id ?? 0) && !isSlideValid) {
        submitStepForm()
        // On remet l'url à jour pour indiquer qu'on est toujours sur la même slide
        globalThis.history.replaceState(null, '', `#${currentSlide?.id}`)
      } else {
        goTo(slideId, true)
      }
    }

    globalThis.addEventListener('popstate', handlePopState)
    return () => globalThis.removeEventListener('popstate', handlePopState)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- abonnement volontairement stable, cf. commentaire ci-dessus (valeurs lues via refs)
  }, [])

  // on rajoute à l'historique du navigateur la slide actuelle si besoin
  useEffect(() => {
    if (needToAddHistory) {
      globalThis.history.pushState(null, '', `#${currentSlide?.id}`)
      removeNeedAddHistory()
    }
  }, [needToAddHistory, currentSlide, removeNeedAddHistory])

  // Tracking slide changed (skipped on initial render and when same slide)
  useEffect(() => {
    if (!currentSlide) return

    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
      previousSlideIdRef.current = currentSlide.id
      return
    }

    if (previousSlideIdRef.current === currentSlide.id) return

    previousSlideIdRef.current = currentSlide.id

    const stepperLabel = stepper.find((x) => x.position === currentSlide.stepperPosition)?.label
    const question = getFirstQuestionOfSlide(currentSlide)
    const firstQuestionTitle = question?.label
    const slideFirstQuestionExtraInfo = question?.extraInfo

    // Si on n'a pas réussi à récupérer le label du stepper ou de la première question, on ne track pas l'abandon pour éviter d'envoyer des données incomplètes
    if (!stepperLabel || !firstQuestionTitle || !slideFirstQuestionExtraInfo) return

    trackSlideChanged({
      slideFirstQuestionTitle: firstQuestionTitle,
      slideTitle: stepperLabel,
      slideFirstQuestionExtraInfo: slideFirstQuestionExtraInfo,
    })
  }, [currentSlide, stepper, trackSlideChanged])

  useEffect(() => {
    if (!currentSlide) return

    // à chaque fois que la currentStep est mise à jour on l'ajoute à notre état local, avec le statut 'showing'
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- machine à états d'animation pilotée par le changement de currentSlide : la déplacer au render décalerait le statut 'showing' d'un cycle et casserait l'animation d'entrée
    setSlides((prev) => {
      // on défini la slide à mettre dans steps
      const slideToAdd = {
        slide: currentSlide,
        status: 'showing',
      } as StatefulSlide

      // si il n'y avait pas de slide de stockée, on génère un tableau qui contient la slide et pas d'animation (car il n'y a pas de slide précédente)
      if (prev.length === 0) {
        slideToAdd.status = 'displayed'
        return [slideToAdd]
      }

      // sinon, on check si la slide était déjà présente dans steps
      const slideInSteps = prev.find((slide) => slide.slide.id === currentSlide.id)

      const nextState = [...prev]
      if (!slideInSteps) {
        // si elle n'était pas présente, on l'ajoute
        nextState.push(slideToAdd)
      }

      const slideCurrentlyDisplayed = prev.find((slide) => slide.status === 'displayed')

      return nextState.map((slide) => {
        if (slide.slide.id === currentSlide.id) {
          return {
            ...slideToAdd,
            status: slideCurrentlyDisplayed?.slide.id === currentSlide.id ? 'displayed' : 'showing',
          }
        } else if (slide.status === 'displayed') {
          return { ...slide, status: 'hiding' }
        } else {
          return { ...slide, status: 'hidden' }
        }
      })
    })

    const resolveSlideStatus = (slide: StatefulSlide): StatefulSlide => {
      if (slide.status === 'hiding') return { ...slide, status: 'hidden' }
      if (slide.status === 'showing') return { ...slide, status: 'displayed' }
      return slide
    }

    const timeout = setTimeout(() => {
      setSlides((prev) => prev.map(resolveSlideStatus))
      // on met 410ms de timeout pour le temps d'animation,
      // le focus se fait uniquement si slideAnimation vaut 'fromLeft' ou 'fromRight' ou 'fromTop' ou 'fromBottom', donc il ne faut pas changer sa valeur trop tôt
      // (valeur qui est basée sur 'status' de la slide)

      // Le focus et le scroll sont gérés par le composant Slide du DS
      // (focus({ preventScroll: true }) + window.scrollTo pour fiabilité cross-browser)
    }, 410)
    return () => clearTimeout(timeout)
  }, [currentSlide])

  return (
    <SliderContainer isLoading={!currentSlide}>
      <>
        {slides.map(({ slide, status }) => {
          if (status === 'hidden') return null
          return (
            <Fragment key={slide.id}>
              <Slide slideAnimation={getSlideAnimation(status, direction, isVertical)}>
                <QuestionManagerForm
                  currentSlide={slide}
                  isLastSlide={slide.isLastSlide}
                  toggleNextIsLoading={toggleNextIsLoading}
                />
              </Slide>
            </Fragment>
          )
        })}
      </>
    </SliderContainer>
  )
}

export default Slides
